/* Slideshow — stacked pop-crossfade hero.
   Slides share one grid cell and pop in / out in place (no horizontal slide).
   Autoplay is driven by the active dot's progress-ring animation: when the
   ring finishes (ss-dot-fill), advance — so the ring and the advance stay in
   lockstep, and pausing the ring on hover/focus pauses autoplay with it.
   Also plays/pauses background video per active slide + the mute toggle.
   Being a custom element, it re-initialises itself on theme-editor re-render. */
(function () {
  var reducedMotion = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function playVideo(v) {
    if (!v) return;
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  if (customElements.get('lumen-fade-slideshow')) return;

  customElements.define('lumen-fade-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.slideshow__slide'));
      if (!this.slides.length) return;

      // The first slide is server-rendered .is-active (no flash before JS).
      this.index = 0;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].classList.contains('is-active')) { this.index = i; break; }
      }

      this.peek = this.getAttribute('data-layout') === 'peek';
      this.track = this.querySelector('.slideshow__track');

      this._wireMute();

      if (this.slides.length > 1) this._buildDots();
      this._activate(this.index);
      if (this.peek) this._wirePeekScroll();

      var ms = parseInt(this.getAttribute('data-autoplay'), 10);
      this.autoplay = (ms && this.slides.length > 1 && !reducedMotion) ? ms : 0;
      if (this.autoplay) this._startAutoplay();
    }

    disconnectedCallback() {
      if (this._onAnimEnd && this.dotsWrap) this.dotsWrap.removeEventListener('animationend', this._onAnimEnd);
      if (this._onEnter) { this.removeEventListener('mouseenter', this._onEnter); this.removeEventListener('focusin', this._onEnter); }
      if (this._onLeave) { this.removeEventListener('mouseleave', this._onLeave); this.removeEventListener('focusout', this._onLeave); }
    }

    _buildDots() {
      var self = this;
      var wrap = document.createElement('div');
      wrap.className = 'slider__dots';
      wrap.setAttribute('role', 'tablist');
      this.slides.forEach(function (_, i) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'slider__dot';
        d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        d.addEventListener('click', function () { self.goTo(i); });
        wrap.appendChild(d);
      });
      this.appendChild(wrap);
      this.dots = Array.prototype.slice.call(wrap.children);
      this.dotsWrap = wrap;
    }

    _activate(i) {
      this.slides.forEach(function (s, j) {
        var on = j === i;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        var v = s.querySelector('.slideshow__video');
        if (v) { if (on) playVideo(v); else v.pause(); }
      });
      if (this.dots) this.dots.forEach(function (d, j) {
        if (j === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      this.index = i;
      if (this.peek) this._scrollTo(i);
    }

    goTo(i) {
      i = (i + this.slides.length) % this.slides.length;
      if (i === this.index) return;
      this._activate(i);
    }

    next() { this.goTo(this.index + 1); }

    /* Peek layout: bring slide i to centre, and briefly ignore the resulting
       programmatic scroll so the sync handler doesn't fight it. */
    _scrollTo(i) {
      var track = this.track, slide = this.slides[i];
      if (!track || !slide) return;
      var target = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
      var self = this;
      this._suppressScroll = true;
      track.scrollTo({ left: target, behavior: reducedMotion ? 'auto' : 'smooth' });
      clearTimeout(this._scrollUnlock);
      this._scrollUnlock = setTimeout(function () { self._suppressScroll = false; }, 700);
    }

    /* Peek layout: when the visitor swipes, snap the active dot/video to
       whichever slide is nearest centre (without re-scrolling). */
    _wirePeekScroll() {
      var self = this, track = this.track;
      if (!track) return;
      track.addEventListener('scroll', function () {
        if (self._suppressScroll) return;
        clearTimeout(self._scrollSync);
        self._scrollSync = setTimeout(function () {
          var center = track.scrollLeft + track.clientWidth / 2, best = self.index, bestD = Infinity;
          self.slides.forEach(function (s, j) {
            var d = Math.abs((s.offsetLeft + s.clientWidth / 2) - center);
            if (d < bestD) { bestD = d; best = j; }
          });
          if (best === self.index) return;
          self.slides.forEach(function (s, j) {
            var on = j === best;
            s.classList.toggle('is-active', on);
            s.setAttribute('aria-hidden', on ? 'false' : 'true');
            var v = s.querySelector('.slideshow__video');
            if (v) { if (on) playVideo(v); else v.pause(); }
          });
          if (self.dots) self.dots.forEach(function (d, j) {
            if (j === best) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
          });
          self.index = best;
        }, 120);
      }, { passive: true });
    }

    _startAutoplay() {
      var self = this;
      // Advance when the active dot's ring finishes. Moving aria-current in
      // _activate restarts the next dot's ring, so the cycle self-perpetuates.
      this._onAnimEnd = function (e) {
        if (e.animationName === 'ss-dot-fill') self.next();
      };
      this.dotsWrap.addEventListener('animationend', this._onAnimEnd);

      // Pause on hover/focus — .is-paused pauses the ring in CSS, which stops
      // animationend firing, so autoplay pauses and resumes perfectly in sync.
      this._onEnter = function () { self.classList.add('is-paused'); };
      this._onLeave = function () { self.classList.remove('is-paused'); };
      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusin', this._onEnter);
      this.addEventListener('focusout', this._onLeave);
    }

    _wireMute() {
      this.querySelectorAll('[data-slideshow-mute]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var slide = btn.closest('.slideshow__slide');
          var video = slide && slide.querySelector('.slideshow__video');
          if (!video) return;
          video.muted = !video.muted;
          btn.setAttribute('data-muted', video.muted ? 'true' : 'false');
          btn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
          if (!video.muted) playVideo(video);
        });
      });
    }
  });
})();
